import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';

import {
    CustomRoleDetail,
    CustomRoleUpsertRequest,
    PermissionCatalog,
    PermissionItem,
    StitchedRole
} from '../tenant-role.model';
import { TenantRoleService } from '../tenant-role.service';

@Component({
    selector: 'app-custom-role-builder',
    templateUrl: './custom-role-builder.component.html',
    styleUrls: ['./custom-role-builder.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        DialogModule,
        CheckboxModule,
        InputTextModule,
        TagModule,
        ToastModule,
        TooltipModule
    ],
    providers: [MessageService]
})
export class CustomRoleBuilderComponent implements OnInit, OnChanges {
    @Input() visible: boolean = false;
    @Input() roleId: number | null = null;
    @Output() closed = new EventEmitter<boolean>();

    roleName: string = '';
    description: string = '';
    roleScope: string = 'COMPANY';

    standardRoles: StitchedRole[] = [];
    selectedStandardRoleIds = new Set<number>();

    permissionCatalogs: PermissionCatalog[] = [];
    allPermissions: PermissionItem[] = [];

    // All currently selected permissions (union of stitched + handpicked)
    selectedPermissionIds = new Set<number>();

    // Explicitly handpicked additions that didn't come from any selected standard role
    handpickedPermissionIds = new Set<number>();

    activeModule: string = 'All';
    searchTerm: string = '';
    permissionFilter: 'ALL' | 'SELECTED' | 'STITCHED' | 'HANDPICKED' = 'ALL';

    isLoading: boolean = false;
    isSaving: boolean = false;

    constructor(
        private tenantRoleService: TenantRoleService,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.loadCatalogData();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['visible'] && this.visible) {
            if (this.roleId) {
                this.loadExistingRole(this.roleId);
            } else {
                this.resetForm();
            }
        }
    }

    loadCatalogData(): void {
        this.isLoading = true;
        this.tenantRoleService.getStandardRoles().subscribe({
            next: roles => {
                this.standardRoles = roles ?? [];
                this.loadPermissions();
            },
            error: err => {
                console.error('Failed to load standard roles', err);
                this.isLoading = false;
            }
        });
    }

    loadPermissions(): void {
        this.tenantRoleService.getPermissionsCatalog().subscribe({
            next: catalogs => {
                this.permissionCatalogs = catalogs ?? [];
                this.allPermissions = [];
                for (const cat of this.permissionCatalogs) {
                    if (cat.permissions) {
                        this.allPermissions.push(...cat.permissions);
                    }
                }
                this.isLoading = false;
            },
            error: err => {
                console.error('Failed to load permissions catalog', err);
                this.isLoading = false;
            }
        });
    }

    loadExistingRole(id: number): void {
        this.isLoading = true;
        this.tenantRoleService.getRoleDetail(id).subscribe({
            next: role => {
                this.roleName = role.roleName;
                this.description = role.description || '';
                this.roleScope = role.roleScope || 'COMPANY';

                this.selectedStandardRoleIds = new Set(
                    (role.stitchedRoles || []).map(r => r.roleId)
                );

                this.selectedPermissionIds = new Set(
                    (role.permissions || []).map(p => p.permissionId)
                );

                this.handpickedPermissionIds = new Set(
                    role.handpickedPermissionIds || []
                );

                this.isLoading = false;
            },
            error: err => {
                console.error('Failed to load role detail', err);
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Could not load role details.' });
                this.isLoading = false;
            }
        });
    }

    resetForm(): void {
        this.roleName = '';
        this.description = '';
        this.roleScope = 'COMPANY';
        this.selectedStandardRoleIds.clear();
        this.selectedPermissionIds.clear();
        this.handpickedPermissionIds.clear();
        this.searchTerm = '';
        this.activeModule = 'All';
        this.permissionFilter = 'ALL';
    }

    // --- Multi-Standard-Role Stitching ---

    isStandardRoleSelected(roleId: number): boolean {
        return this.selectedStandardRoleIds.has(roleId);
    }

    toggleStandardRole(role: StitchedRole): void {
        const id = role.roleId;
        if (this.selectedStandardRoleIds.has(id)) {
            this.selectedStandardRoleIds.delete(id);
        } else {
            this.selectedStandardRoleIds.add(id);
        }
        this.recomputePermissionsFromStitchedRoles();
    }

    private recomputePermissionsFromStitchedRoles(): void {
        const stitchedPermSet = new Set<number>();
        this.selectedStandardRoleIds.forEach(roleId => {
            const standard = this.standardRoles.find(r => r.roleId === roleId);
            if (standard?.permissionIds) {
                for (const pid of standard.permissionIds) {
                    stitchedPermSet.add(pid);
                }
            }
        });

        // Selected permissions = stitched + handpicked additions
        const newSelected = new Set<number>(stitchedPermSet);
        this.handpickedPermissionIds.forEach(hpId => {
            newSelected.add(hpId);
        });
        this.selectedPermissionIds = newSelected;
    }

    // --- Handpicked Permissions ---

    isPermissionSelected(permId: number): boolean {
        return this.selectedPermissionIds.has(permId);
    }

    togglePermission(perm: PermissionItem): void {
        const id = perm.permissionId;
        if (this.selectedPermissionIds.has(id)) {
            this.selectedPermissionIds.delete(id);
            this.handpickedPermissionIds.delete(id);
        } else {
            this.selectedPermissionIds.add(id);
            // If it is not part of any currently selected standard role, record as handpicked
            if (!this.isPermInAnyStitchedRole(id)) {
                this.handpickedPermissionIds.add(id);
            }
        }
    }

    isPermInAnyStitchedRole(permId: number): boolean {
        let found = false;
        this.selectedStandardRoleIds.forEach(roleId => {
            const standard = this.standardRoles.find(r => r.roleId === roleId);
            if (standard?.permissionIds?.includes(permId)) {
                found = true;
            }
        });
        return found;
    }

    getStitchedSourceRoleName(permId: number): string | null {
        let sourceName: string | null = null;
        this.selectedStandardRoleIds.forEach(roleId => {
            if (!sourceName) {
                const standard = this.standardRoles.find(r => r.roleId === roleId);
                if (standard?.permissionIds?.includes(permId)) {
                    sourceName = standard.roleName;
                }
            }
        });
        return sourceName;
    }

    get modules(): string[] {
        const list = this.permissionCatalogs.map(c => c.module);
        return ['All', ...list];
    }

    get filteredPermissions(): PermissionItem[] {
        let list = this.allPermissions;

        if (this.activeModule !== 'All') {
            list = list.filter(p => p.module === this.activeModule);
        }

        const query = this.searchTerm.trim().toLowerCase();
        if (query) {
            list = list.filter(p =>
                (p.permissionName || '').toLowerCase().includes(query) ||
                (p.permissionCode || '').toLowerCase().includes(query) ||
                (p.description || '').toLowerCase().includes(query) ||
                (p.resource || '').toLowerCase().includes(query)
            );
        }

        if (this.permissionFilter === 'SELECTED') {
            list = list.filter(p => this.selectedPermissionIds.has(p.permissionId));
        } else if (this.permissionFilter === 'STITCHED') {
            list = list.filter(p => this.isPermInAnyStitchedRole(p.permissionId));
        } else if (this.permissionFilter === 'HANDPICKED') {
            list = list.filter(p => this.handpickedPermissionIds.has(p.permissionId));
        }

        return list;
    }

    get countFromStitchedRoles(): number {
        let count = 0;
        this.selectedPermissionIds.forEach(pid => {
            if (this.isPermInAnyStitchedRole(pid)) count++;
        });
        return count;
    }

    get countHandpicked(): number {
        let count = 0;
        this.selectedPermissionIds.forEach(pid => {
            if (!this.isPermInAnyStitchedRole(pid)) count++;
        });
        return count;
    }

    selectAllInActiveModule(): void {
        for (const perm of this.filteredPermissions) {
            this.selectedPermissionIds.add(perm.permissionId);
            if (!this.isPermInAnyStitchedRole(perm.permissionId)) {
                this.handpickedPermissionIds.add(perm.permissionId);
            }
        }
    }

    clearAllInActiveModule(): void {
        for (const perm of this.filteredPermissions) {
            this.selectedPermissionIds.delete(perm.permissionId);
            this.handpickedPermissionIds.delete(perm.permissionId);
        }
    }

    // --- Save & Cancel ---

    onSave(): void {
        if (!this.roleName.trim()) {
            this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Role name is required.' });
            return;
        }

        if (this.selectedPermissionIds.size === 0) {
            this.messageService.add({ severity: 'warn', summary: 'Validation', detail: 'Select at least one permission or stitch a standard role.' });
            return;
        }

        this.isSaving = true;
        const request: CustomRoleUpsertRequest = {
            roleName: this.roleName.trim(),
            description: this.description.trim(),
            roleScope: this.roleScope,
            stitchedStandardRoleIds: Array.from(this.selectedStandardRoleIds),
            handpickedPermissionIds: Array.from(this.handpickedPermissionIds)
        };

        const observable = this.roleId
            ? this.tenantRoleService.updateCustomRole(this.roleId, request)
            : this.tenantRoleService.createCustomRole(request);

        observable.subscribe({
            next: () => {
                this.isSaving = false;
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: `Custom role '${this.roleName}' saved successfully.`
                });
                this.close(true);
            },
            error: err => {
                this.isSaving = false;
                const errorMsg = err?.error?.message || err?.message || 'Failed to save custom role.';
                this.messageService.add({ severity: 'error', summary: 'Save Failed', detail: errorMsg });
            }
        });
    }

    close(saved: boolean = false): void {
        this.visible = false;
        this.closed.emit(saved);
    }
}

